import { apiClient } from '../lib/apiClient';

export interface EventData {
    id: number;
    txHash: string;
    eventName: string;
    eventData: {
        [key: string]: any;
    };
    blockNumber: number;
    logIndex: number;
    eventTimestamp: number; // Unix timestamp in seconds
    contractAddress: string;
    createdAt: string;
    updatedAt: string;
}

// API Response interfaces matching the backend structure
interface ApiTotalEventsResponse {
    success: boolean;
    data: {
        filteredEvents: number;
        totalEvents: number;
    };
    message: string;
}

interface ApiTotalMusterRollsResponse {
    success: boolean;
    data: {
        filteredMusterRolls: number;
        totalMusterRolls: number;
    };
    message: string;
}

interface ApiGeographyCountsResponse {
    success: boolean;
    data: {
        totalStates: number;
        totalDistricts: number;
    };
    message: string;
}

interface ApiGetEventsResponse {
    success: boolean;
    data: {
        data: EventData[];
        total: number;
        perPage: number;
        currentPage: number;
        lastPage: number;
        nextPage: number | null;
        prevPage: number | null;
    };
    message: string;
}

// Service interfaces for internal use
export interface TotalEventsResponse {
    filteredEvents: number;
    totalEvents: number;
}

export interface TotalMusterRollsResponse {
    filteredMusterRolls: number;
    totalMusterRolls: number;
}

export interface GeographyCountsResponse {
    totalStates: number;
    totalDistricts: number;
}

export interface State {
    id: number;
    name: string;
    stateCode: string;
    createdAt: string;
    updatedAt: string;
}

export interface District {
    id: number;
    name: string;
    stateId: number;
    districtCode: string;
    createdAt: string;
    updatedAt: string;
}

interface ApiStatesResponse {
    success: boolean;
    data: State[];
    message: string;
}

interface ApiDistrictsResponse {
    success: boolean;
    data: District[];
    message: string;
}

export interface GetEventsRequest {
    page: number;
    pageSize: number;
    filterType: '1h' | '24h' | '7d' | '30d' | 'custom';
    fromDate?: string; // Format: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
    toDate?: string; // Format: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
    eventNames?: string[]; // Optional array of event names
    stateId?: number; // Optional state ID filter
}

export interface SearchTransactionsRequest {
    page: number;
    perPage: number;
    startDate?: string; // Format: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
    endDate?: string; // Format: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
    eventNames?: string[]; // Optional array of event names
    stateId?: number; // Optional state ID filter
}

export interface GetEventsResponse {
    events: EventData[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

const formatDate = (date: any) => {
    if (!date) return null;
    return new Date(date).toISOString().split('T')[0];
};

export const eventsService = {
    /**
     * Get total number of events with optional date filters
     */
    async getTotalEvents(startDate?: string, endDate?: string): Promise<TotalEventsResponse> {
        // Build query parameters - extract date part (YYYY-MM-DD) from ISO string
        const params = new URLSearchParams();
        if (startDate) {
            const dateOnly = startDate.split('T')[0]; // Extract YYYY-MM-DD from ISO string
            params.append('startDate', dateOnly);
        }
        if (endDate) {
            const dateOnly = endDate.split('T')[0]; // Extract YYYY-MM-DD from ISO string
            params.append('endDate', dateOnly);
        }

        const queryString = params.toString();
        const url = 'api/analytics/total-events-counts';

        const response = await apiClient<ApiTotalEventsResponse>(url, {
            method: 'POST',
            body: {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
            },
        });

        return {
            filteredEvents: response.data.filteredEvents,
            totalEvents: response.data.totalEvents,
        };
    },

    /**
     * Get total number of muster rolls with optional date filters
     */
    async getTotalMusterRolls(startDate?: string, endDate?: string): Promise<TotalMusterRollsResponse> {
        // Build query parameters - extract date part (YYYY-MM-DD) from ISO string
        const params = new URLSearchParams();
        if (startDate) {
            const dateOnly = startDate.split('T')[0]; // Extract YYYY-MM-DD from ISO string
            params.append('startDate', dateOnly);
        }
        if (endDate) {
            const dateOnly = endDate.split('T')[0]; // Extract YYYY-MM-DD from ISO string
            params.append('endDate', dateOnly);
        }

        const queryString = params.toString();
        const url = 'api/analytics/muster-roll-counts';

        const response = await apiClient<ApiTotalMusterRollsResponse>(url, {
            method: 'POST',
            body: {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
            },
        });

        return {
            filteredMusterRolls: response.data.filteredMusterRolls,
            totalMusterRolls: response.data.totalMusterRolls,
        };
    },

    /**
     * Get geography counts (total states and districts)
     */
    async getGeographyCounts(): Promise<GeographyCountsResponse> {
        const response = await apiClient<ApiGeographyCountsResponse>('api/analytics/geography-counts', {
            method: 'GET',
        });

        return {
            totalStates: response.data.totalStates,
            totalDistricts: response.data.totalDistricts,
        };
    },

    /**
     * Get list of events with filters and pagination
     */
    async getEvents(request: GetEventsRequest): Promise<GetEventsResponse> {
        const response = await apiClient<ApiGetEventsResponse>('api/events', {
            method: 'POST',
            body: request,
        });

        return {
            events: response.data.data,
            totalCount: response.data.total,
            page: response.data.currentPage,
            pageSize: response.data.perPage,
            totalPages: response.data.lastPage,
        };
    },

    /**
     * Search transactions with filters and pagination
     */
    async searchTransactions(request: GetEventsRequest): Promise<GetEventsResponse> {
        // Transform request to match transactions API format
        const { pageSize, filterType, fromDate, toDate, ...rest } = request;
        const transactionsRequest: SearchTransactionsRequest = {
            ...rest,
            perPage: pageSize,
            ...(fromDate ? { startDate: fromDate } : {}),
            ...(toDate ? { endDate: toDate } : {}),
        };

        const response = await apiClient<ApiGetEventsResponse>('api/transactions/search', {
            method: 'POST',
            body: transactionsRequest,
        });

        return {
            events: response.data.data,
            totalCount: response.data.total,
            page: response.data.currentPage,
            pageSize: response.data.perPage,
            totalPages: response.data.lastPage,
        };
    },

    /**
     * Get event details by transaction hash
     */
    async getEventByTxHash(txHash: string): Promise<EventData> {
        const response = await apiClient<{ success: boolean; data: { data: EventData[] }; message: string }>(`api/events/${txHash}`, {
            method: 'GET',
        });

        // The API returns an array with a single event in data.data
        return response.data.data[0];
    },

    /**
     * Get list of states
     */
    async getStates(): Promise<State[]> {
        const response = await apiClient<ApiStatesResponse>('api/transactions/states', {
            method: 'GET',
        });

        return response.data;
    },

    /**
     * Get list of districts by state ID
     */
    async getDistrictsByState(stateId: number): Promise<District[]> {
        const response = await apiClient<ApiDistrictsResponse>(`api/transactions/districts/${stateId}`, {
            method: 'GET',
        });

        return response.data;
    },
};
